import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MdDownload } from 'react-icons/md';
import api from '../lib/axios';
import { VisitorResponse } from '../types';
import { endpoints } from '../constants/endpoints';
import FlagChatModal from './FlagChatModal';

interface ChatHeaderProps {
  visitor: VisitorResponse;
  onFlagChange: (isFlagged: boolean) => void;
}

export default function ChatHeader({ visitor, onFlagChange }: ChatHeaderProps) {
  const queryClient = useQueryClient();
  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);

  const displayName =
    visitor.first_name && visitor.surname
      ? `${visitor.first_name} ${visitor.surname}`
      : visitor.visitor_id;

  const unflagMutation = useMutation({
    mutationFn: (visitorId: string) =>
      api.post(endpoints.chat.unflag(visitorId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visitors'] });
      queryClient.invalidateQueries({
        queryKey: ['messages', visitor.visitor_id],
      });
      onFlagChange(false);
      toast.success('Chat unflagged');
    },
    onError: () => {
      toast.error('Failed to unflag chat');
    },
  });

  const exportMutation = useMutation({
    mutationFn: async (visitorId: string) => {
      const blob = await api.get(endpoints.chat.exportPDF(visitorId), {
        responseType: 'blob',
      });
      return blob as unknown as Blob;
    },
    onSuccess: (blob: Blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chat_export_${displayName.replace(/\s+/g, '_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Chat exported');
    },
    onError: () => {
      toast.error('Failed to export chat');
    },
  });

  return (
    <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
      <h3 className="text-sm font-medium text-gray-900">
        {displayName}
        {visitor.email && (
          <span className="ml-2 font-normal text-gray-500">
            ({visitor.email})
          </span>
        )}
        {visitor.member_number && (
          <span className="ml-2 font-normal text-gray-500">
            Member #{visitor.member_number}
          </span>
        )}
      </h3>
      <div className="flex items-center gap-2">
        <button
          onClick={() => exportMutation.mutate(visitor.visitor_id)}
          disabled={exportMutation.isPending}
          className="flex items-center gap-1 rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
        >
          <MdDownload className="text-sm" />
          {exportMutation.isPending ? 'Exporting...' : 'Export PDF'}
        </button>
        {!visitor.is_flagged && (
          <button
            onClick={() => setIsFlagModalOpen(true)}
            className="w-20 rounded-full border border-red-300 bg-red-50 px-3 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 cursor-pointer"
          >
            Flag
          </button>
        )}
        {visitor.is_flagged && (
          <button
            onClick={() => unflagMutation.mutate(visitor.visitor_id)}
            disabled={unflagMutation.isPending}
            className="w-20 rounded-full border border-red-300 bg-red-50 px-3 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 cursor-pointer"
          >
            {unflagMutation.isPending ? 'Unflagging...' : 'Unflag'}
          </button>
        )}
      </div>
      <FlagChatModal
        isOpen={isFlagModalOpen}
        visitorId={visitor.visitor_id}
        onClose={() => setIsFlagModalOpen(false)}
        onFlagged={() => onFlagChange(true)}
      />
    </div>
  );
}
